const QUERY_PARAMS_STORAGE_KEY = 'snappaisa_query_params';

const TRACKED_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'clickid',
];

/**
 * Get stored query parameters from localStorage
 * @returns {Object} Object containing all stored query parameters
 */
export const getStoredQueryParams = () => {
  try {
    const storedParamsStr = localStorage.getItem(QUERY_PARAMS_STORAGE_KEY);
    if (storedParamsStr) return JSON.parse(storedParamsStr);
  } catch (error) {
    console.warn('Failed to parse stored query params:', error);
  }
  return {};
};

/**
 * Store only tracked query parameters
 * @param {Object} params - Object containing query parameters to store
 */
export const storeQueryParams = (params) => {
  try {
    const existingParams = getStoredQueryParams();
    const mergedParams = { ...existingParams };

    Object.entries(params || {}).forEach(([key, value]) => {
      if (TRACKED_QUERY_KEYS.includes(key) && value) {
        mergedParams[key] = String(value);
      }
    });

    localStorage.setItem(QUERY_PARAMS_STORAGE_KEY, JSON.stringify(mergedParams));
  } catch (error) {
    console.warn('Failed to store query params:', error);
  }
};

/**
 * Pick tracked params from URL search string. Normalizes click_id -> clickid.
 * @param {string} search - Search string (e.g. ?utm_source=x&click_id=abc)
 * @returns {Object} Picked params (click_id stored as clickid)
 */
export const pickTrackedParamsFromSearch = (search) => {
  const searchParams = new URLSearchParams(search || '');
  const picked = {};

  TRACKED_QUERY_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) picked[key] = value;
  });

  // Also accept click_id from URL and store as clickid
  const clickIdFromUrl = searchParams.get('click_id');
  if (clickIdFromUrl && !picked.clickid) {
    picked.clickid = clickIdFromUrl;
  }

  return picked;
};

/**
 * Capture tracked params from current URL search and store in localStorage
 * @param {string} search - Current location.search
 */
export const captureAndStoreQueryParamsFromSearch = (search) => {
  const picked = pickTrackedParamsFromSearch(search);
  if (Object.keys(picked).length > 0) storeQueryParams(picked);
};

export const normalizeSearch = (search) =>
  new URLSearchParams((search || '').replace(/^\?/, '')).toString();

/**
 * Merge current URL search with stored params (fill in missing tracked params)
 * @param {string} search - Current location.search
 * @returns {string} New search string with merged params (e.g. "?utm_source=x&clickid=abc")
 */
export const mergeSearchWithStored = (search) => {
  const existingParams = new URLSearchParams((search || '').replace(/^\?/, ''));
  const storedParams = getStoredQueryParams();

  TRACKED_QUERY_KEYS.forEach((key) => {
    if (existingParams.get(key)) return;
    const storedValue = storedParams[key];
    if (storedValue) existingParams.set(key, storedValue);
  });

  const queryString = existingParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Append stored query parameters to a path. Existing query params take precedence.
 * @param {string} path - The path or URL to append query params to
 * @returns {string} Path with query parameters appended
 */
export const appendStoredQueryParams = (path) => {
  const storedParams = getStoredQueryParams();
  if (Object.keys(storedParams).length === 0) return path;

  const [basePath, existingQuery] = String(path).split('?');
  const existingParams = existingQuery
    ? new URLSearchParams(existingQuery)
    : new URLSearchParams();

  TRACKED_QUERY_KEYS.forEach((key) => {
    const storedValue = storedParams[key];
    if (storedValue && !existingParams.get(key)) {
      existingParams.set(key, storedValue);
    }
  });

  const queryString = existingParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Get a specific query parameter value from stored params
 * @param {string} key - The query parameter key
 * @returns {string|null} The value or null if not found
 */
export const getStoredQueryParam = (key) => {
  const storedParams = getStoredQueryParams();
  return storedParams[key] || null;
};
